import { Currency, CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/CurrencyLogo'
import FiatValue from 'components/FiatValue'
import Loader from 'components/Loader'
import { RowBetween } from 'components/Row'
import { MouseoverTooltip } from 'components/Tooltip'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import { useContext, useMemo } from 'react'
import { useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'

import { StakingData } from '../config'
import { IslandContext } from '../context/IslandContext'
import * as S from '../styleds'
import AutoCompoundInfoBanner from './components/AutoCompoundInfo'

export type StakeInterface = {
  kekId?: string
  liquidity: CurrencyAmount<Token> | undefined
  endingTimestamp?: Date
  lockMultiplier?: number
}

export type Stakes = StakeInterface[] | null | undefined

export type UnderlyingBalances = {
  amount0: CurrencyAmount<Currency> | undefined
  amount1: CurrencyAmount<Currency> | undefined
}

interface BasePosition {
  liquidity: CurrencyAmount<Token> | undefined
  liquidityUSD: number | undefined
  underlyingBalances: UnderlyingBalances
}

export interface KodiakFarmPosition extends BasePosition {
  stakes: Stakes
}

export interface RewardVaultPosition extends BasePosition {
  stakes: Stakes
}

export interface BaultPosition extends BasePosition {
  positions?: any
}

export type StakingPositions = {
  kodiakFarm?: KodiakFarmPosition
  rewardVault?: RewardVaultPosition
  bault?: BaultPosition
}

export type UserBalanceData = {
  shares: CurrencyAmount<Currency> | undefined
  sharesUSD: number | undefined
  underlyingBalances: UnderlyingBalances
}

export type TokenInformation = {
  poolCurrency: Currency | undefined
  currencyA: Currency | null
  currencyB: Currency | null
  token0: Token | null | undefined
  token1: Token | null | undefined
}

export type IslandPositionCardProps = {
  userBalanceShares: CurrencyAmount<Currency> | undefined
  userBalanceSharesUSD: number | undefined
  userUnderlyingBalances: UnderlyingBalances

  combinedUserBalanceShares: CurrencyAmount<Currency> | undefined
  combinedUserBalanceSharesUSD: number | undefined
  combinedUserUnderlyingBalances: UnderlyingBalances

  stakingPositions: StakingPositions

  // Token information
  poolCurrency: Currency | undefined
  currencyA: Currency | null
  currencyB: Currency | null
  token0: Token | null | undefined
  token1: Token | null | undefined

  isLoading?: boolean
}

const Position = ({
  name,
  balance,
  stakingBalance,
  stakingToken,
  tooltip = true,
  isLoading = false,
}: {
  name: string
  balance?: CurrencyAmount<Currency>
  stakingBalance?: CurrencyAmount<Currency>
  stakingToken?: Currency
  tooltip?: boolean
  isLoading?: boolean
}) => {
  const theme = useTheme()

  const { value: balanceUSD } = useApiTokenValue(balance as CurrencyAmount<Token>)
  const { value: stakingBalanceUSD } = useApiTokenValue(stakingBalance as CurrencyAmount<Token>)

  const totalTokenAmount = useMemo(() => {
    if (!balance && !stakingBalance) return undefined
    if (!balance) return stakingBalance
    if (!stakingBalance) return balance

    if (balance.currency.equals(stakingBalance.currency)) {
      return balance.add(stakingBalance)
    }
    return stakingBalance
  }, [balance, stakingBalance])

  const showCombined = !!balance && !!stakingBalance
  const totalUSD = showCombined ? [balanceUSD, stakingBalanceUSD] : stakingBalanceUSD || balanceUSD

  return (
    <AutoColumn>
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="14px" color={theme.textPrimary}>
        {name}
      </ThemedText.DeprecatedBody>

      <ThemedText.DeprecatedBody fontSize="20px" fontWeight="400" lineHeight="28px" color={theme.textPrimary}>
        {isLoading ? (
          <Loader size="14px" />
        ) : (
          <MouseoverTooltip
            disableHover={!tooltip || !showCombined}
            text={
              showCombined ? (
                <>
                  Wallet: <FiatValue value={balanceUSD} />
                  <br />
                  {name === 'My Deposits' ? 'Total Staked' : name}: <FiatValue value={stakingBalanceUSD} />
                </>
              ) : (
                <>{name} balance</>
              )
            }
          >
            <FiatValue value={totalUSD} />
          </MouseoverTooltip>
        )}
      </ThemedText.DeprecatedBody>

      <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="20px" color={theme.textSecondary}>
        {isLoading ? (
          <Loader size="12px" />
        ) : (
          <MouseoverTooltip
            disableHover={!tooltip || !showCombined}
            text={
              showCombined ? (
                <>
                  Wallet: {balance?.toSignificant(6) || '0'}
                  <br />
                  {name === 'My Deposits' ? 'Total Staked' : name}: {stakingBalance?.toSignificant(6) || '0'}
                </>
              ) : (
                <>
                  {name}: {(stakingBalance || balance)?.toSignificant(6) || '0'}
                </>
              )
            }
          >
            {formatCurrencyAmount(totalTokenAmount, 5) || '0.00'} {stakingToken?.symbol || ''}
          </MouseoverTooltip>
        )}
      </ThemedText.DeprecatedBody>
    </AutoColumn>
  )
}

const useCalculatePositionValue = (stakes: any[] | undefined) => {
  return useMemo(() => {
    if (!stakes || stakes.length === 0) return undefined

    return stakes.reduce((acc, stake) => {
      if (!stake?.liquidity) return acc
      if (!acc) return stake.liquidity
      return acc.add(stake.liquidity)
    }, undefined as CurrencyAmount<Token> | undefined)
  }, [stakes])
}

export default function IslandPositionCard({
  userBalanceShares,
  userBalanceSharesUSD,
  userUnderlyingBalances,
  combinedUserBalanceShares,
  combinedUserBalanceSharesUSD,
  combinedUserUnderlyingBalances,
  stakingPositions,
  poolCurrency,
  currencyA,
  currencyB,
  token0,
  token1,
  isLoading = false,
}: IslandPositionCardProps) {
  const { baultAddress, rewardVault, poolApiResponse, isRewardVaultStakingDisabled } = useContext(IslandContext)
  const theme = useTheme()

  const kodiakFarmPosition = stakingPositions.kodiakFarm
  const rewardVaultPosition = stakingPositions.rewardVault
  const baultPosition = stakingPositions.bault

  const stakingType = useMemo(() => {
    if (kodiakFarmPosition?.liquidity && !kodiakFarmPosition.liquidity.equalTo('0')) return 'kodiakFarm'
    if (rewardVaultPosition?.liquidity && !rewardVaultPosition.liquidity.equalTo('0')) return 'rewardVault'
    return 'none'
  }, [kodiakFarmPosition, rewardVaultPosition]) as StakingData['stakingType']

  const hasAutoCompound = useMemo(() => {
    return baultPosition?.liquidity && !baultPosition.liquidity.equalTo('0')
  }, [baultPosition])

  const totalStakingBalance = useMemo(() => {
    const balances = [kodiakFarmPosition?.liquidity, rewardVaultPosition?.liquidity, baultPosition?.liquidity].filter(
      Boolean
    )

    if (balances.length === 0) return undefined

    return balances.reduce((acc, balance) => {
      if (!acc) return balance
      if (balance && acc.currency.equals(balance.currency)) {
        return acc.add(balance)
      }
      return acc
    }, undefined as CurrencyAmount<Token> | undefined)
  }, [kodiakFarmPosition, rewardVaultPosition, baultPosition])

  const { availableStakes, lockedStakes } = useMemo(() => {
    if (stakingType !== 'kodiakFarm' || !kodiakFarmPosition?.stakes) {
      return { availableStakes: undefined, lockedStakes: undefined }
    }

    const allStakes = kodiakFarmPosition.stakes.filter(
      (stake): stake is StakeInterface => !!stake.liquidity && stake.liquidity.greaterThan('0')
    )
    const now = Date.now()

    const available = allStakes.filter(
      (stake): stake is StakeInterface => !!stake.endingTimestamp && stake.endingTimestamp.getTime() < now
    )
    const locked = allStakes.filter(
      (stake): stake is StakeInterface => !!stake.endingTimestamp && stake.endingTimestamp.getTime() >= now
    )

    return { availableStakes: available, lockedStakes: locked }
  }, [kodiakFarmPosition?.stakes, stakingType])

  const availableFarmBalance = useCalculatePositionValue(availableStakes)
  const lockedFarmBalance = useCalculatePositionValue(lockedStakes)

  return (
    <S.IslandPositionCard>
      <S.IslandPositionDetails>
        <RowBetween>
          <Position
            name="My Deposits"
            balance={userBalanceShares ?? undefined}
            stakingBalance={totalStakingBalance as CurrencyAmount<Currency> | undefined}
            stakingToken={poolCurrency}
            isLoading={isLoading}
          />

          {stakingType === 'kodiakFarm' && (
            <>
              {availableStakes && availableStakes.length > 0 && (
                <Position
                  name="Available"
                  stakingBalance={availableFarmBalance}
                  stakingToken={poolCurrency}
                  tooltip={false}
                  isLoading={isLoading}
                />
              )}

              {lockedStakes && lockedStakes.length > 0 && (
                <Position
                  name="Locked"
                  stakingBalance={lockedFarmBalance}
                  stakingToken={poolCurrency}
                  tooltip={false}
                  isLoading={isLoading}
                />
              )}
            </>
          )}

          {stakingType === 'rewardVault' &&
            rewardVaultPosition?.liquidity &&
            !rewardVaultPosition.liquidity.equalTo('0') && (
              <Position
                name="Staked"
                stakingBalance={rewardVaultPosition.liquidity}
                stakingToken={poolCurrency}
                tooltip={false}
                isLoading={isLoading}
              />
            )}

          {hasAutoCompound && (
            <Position
              name="Auto-Compound"
              stakingBalance={baultPosition?.liquidity ?? undefined}
              stakingToken={baultPosition?.liquidity?.currency || poolCurrency}
              tooltip={false}
              isLoading={isLoading}
            />
          )}
        </RowBetween>
      </S.IslandPositionDetails>

      <AutoColumn gap="12px">
        <S.InnerIslandCardFlex>
          <S.CurrencyFlexBetween>
            <S.FlexRowCenter>
              <CurrencyLogo currency={currencyA} size="20px" />
              <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                {currencyA?.symbol}
              </ThemedText.DeprecatedBody>
            </S.FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
              {isLoading ? (
                <Loader size="14px" />
              ) : (
                <MouseoverTooltip
                  disableHover={!combinedUserUnderlyingBalances?.amount0}
                  text={
                    <>
                      Wallet: {userUnderlyingBalances?.amount0?.toSignificant(6) || '0'}
                      <br />
                      {kodiakFarmPosition?.underlyingBalances?.amount0 && (
                        <>
                          Kodiak Farm: {kodiakFarmPosition.underlyingBalances.amount0.toSignificant(6)}
                          <br />
                        </>
                      )}
                      {rewardVaultPosition?.underlyingBalances?.amount0 && (
                        <>
                          Reward Vault: {rewardVaultPosition.underlyingBalances.amount0.toSignificant(6)}
                          <br />
                        </>
                      )}
                      {baultPosition?.underlyingBalances?.amount0 && (
                        <>
                          Bault: {baultPosition.underlyingBalances.amount0.toSignificant(6)}
                          <br />
                        </>
                      )}
                      Total: {combinedUserUnderlyingBalances?.amount0?.toSignificant(6) || '0'}
                    </>
                  }
                >
                  {combinedUserUnderlyingBalances?.amount0?.toSignificant(6) || '0.00'}
                </MouseoverTooltip>
              )}
            </ThemedText.DeprecatedBody>
          </S.CurrencyFlexBetween>

          <S.CurrencyFlexBetween>
            <S.FlexRowCenter>
              <CurrencyLogo currency={currencyB} size="20px" />
              <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                {currencyB?.symbol}
              </ThemedText.DeprecatedBody>
            </S.FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
              {isLoading ? (
                <Loader size="14px" />
              ) : (
                <MouseoverTooltip
                  disableHover={!combinedUserUnderlyingBalances?.amount1}
                  text={
                    <>
                      Wallet: {userUnderlyingBalances?.amount1?.toSignificant(6) || '0'}
                      <br />
                      {kodiakFarmPosition?.underlyingBalances?.amount1 && (
                        <>
                          Farm: {kodiakFarmPosition.underlyingBalances.amount1.toSignificant(6)}
                          <br />
                        </>
                      )}
                      {rewardVaultPosition?.underlyingBalances?.amount1 && (
                        <>
                          Vault: {rewardVaultPosition.underlyingBalances.amount1.toSignificant(6)}
                          <br />
                        </>
                      )}
                      {baultPosition?.underlyingBalances?.amount1 && (
                        <>
                          Bault: {baultPosition.underlyingBalances.amount1.toSignificant(6)}
                          <br />
                        </>
                      )}
                      Total: {combinedUserUnderlyingBalances?.amount1?.toSignificant(6) || '0'}
                    </>
                  }
                >
                  {combinedUserUnderlyingBalances?.amount1?.toSignificant(6)}
                </MouseoverTooltip>
              )}
            </ThemedText.DeprecatedBody>
          </S.CurrencyFlexBetween>
        </S.InnerIslandCardFlex>

        {!!baultAddress && !isRewardVaultStakingDisabled && (
          <AutoCompoundInfoBanner
            baultAddress={baultAddress}
            rewardVault={rewardVault}
            stakingType={stakingType}
            stakedAmount={
              stakingType === 'rewardVault' && rewardVaultPosition?.liquidity
                ? [{ liquidity: rewardVaultPosition.liquidity }]
                : undefined
            }
            pool={poolApiResponse}
          />
        )}
      </AutoColumn>
    </S.IslandPositionCard>
  )
}
